import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { MermaidDiagram } from "../infrastructure/MermaidDiagram";
import { DocumentHead } from "../../common/DocumentHead";
import { JsonLd } from "../../common/JsonLd";
import { canonicalUrl } from "../../../config";
import { type TemplateEntry } from "../../../data/template-registry";
import { generateMermaidDiagram } from "../../../utils/diagram-generator";
import {
  formatDeploymentMode,
  formatStackConfigKey,
  formatStackConfigValue,
} from "./template-utils";
import { TemplateHero } from "./TemplateHero";
import { TemplateServicesTable } from "./TemplateServicesTable";
import { TemplateLocalDev } from "./TemplateLocalDev";

interface TemplateDetailLayoutProps {
  template: TemplateEntry;
  onBack?: () => void;
}

export const TemplateDetailLayout: React.FC<TemplateDetailLayoutProps> = ({
  template,
  onBack,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { name, description, useCase, slug, stackConfig, deploymentModes } =
    template;

  const pageTitle = `${name} Template | Rusha`;
  const pageDescription = description;
  const diagram = generateMermaidDiagram(template);

  const stackConfigEntries = Object.entries(stackConfig).filter(
    ([, v]) => v !== null && v !== undefined && v !== "none",
  );

  return (
    <Box sx={{ py: 8 }}>
      <DocumentHead
        title={pageTitle}
        description={pageDescription}
        path={`/templates/${slug}`}
        ogType="product"
      />
      <JsonLd
        data={{
          "@type": "SoftwareApplication",
          name: name,
          applicationCategory: "DeveloperApplication",
          operatingSystem: "Web",
          description: description,
          url: canonicalUrl(`/templates/${slug}`),
        }}
      />
      <JsonLd
        data={{
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://rusha.io",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Templates",
              item: "https://rusha.io/templates",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: name,
              item: canonicalUrl(`/templates/${slug}`),
            },
          ],
        }}
      />
      <Container maxWidth="lg">
        {/* Back button */}
        <Box
          sx={{
            mb: 3,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          onClick={onBack || (() => navigate("/templates"))}
        >
          <ArrowBackIcon fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            Back to Templates
          </Typography>
        </Box>

        <TemplateHero template={template} />

        {/* Use Case */}
        <Paper sx={{ p: 3, mb: 4, bgcolor: "background.default" }}>
          <Typography variant="h2" component="h2" gutterBottom fontWeight={600}>
            Use Case
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {useCase}
          </Typography>
        </Paper>

        {/* Tech Stack */}
        {stackConfigEntries.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              fontWeight={600}
            >
              Tech Stack
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {stackConfigEntries.map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${formatStackConfigKey(key)}: ${formatStackConfigValue(value)}`}
                  variant="outlined"
                  size="medium"
                  sx={{
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    "&:hover": { borderColor: theme.palette.primary.main },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Architecture Diagram */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h2" component="h2" gutterBottom fontWeight={600}>
            Architecture
          </Typography>
          <Paper sx={{ p: 3, bgcolor: "background.default" }}>
            <MermaidDiagram diagram={diagram} hideToggle />
          </Paper>
        </Box>

        <TemplateServicesTable template={template} />

        <TemplateLocalDev template={template} />

        {/* Deployment Options */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h2" component="h2" gutterBottom fontWeight={600}>
            Deployment Options
          </Typography>
          <Grid container spacing={2}>
            {deploymentModes.map((mode) => (
              <Grid item xs={12} sm={6} md={4} key={mode}>
                <Paper sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDeploymentMode(mode)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* CTA */}
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h2" component="h2" gutterBottom fontWeight={700}>
            Ready to deploy {name}?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Create an account, choose this template, and deploy in minutes.
          </Typography>
          <Box
            component="a"
            href="https://app.rusha.tierravivaai.net"
            sx={{
              display: "inline-block",
              px: 4,
              py: 1.5,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              textDecoration: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            Get Started
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TemplateDetailLayout;
